import type {
  NormalizedProjectionWithPlayer,
  StreakProjectionItem
} from '@library/types/streaks';
import { useEffect, useMemo, useState } from 'react';
import { useGetStreakProjectionsQuery } from '@bo-services/streakApi';

interface UseStreakProjectionsProps {
  selectedDateString: string;
  selectedLeagueId?: string;
  debouncedTextSearch: string;
}

export const useStreakProjections = ({
  selectedDateString,
  selectedLeagueId,
  debouncedTextSearch
}: UseStreakProjectionsProps) => {
  const [unrankedProjections, setUnrankedProjections] = useState<
    NormalizedProjectionWithPlayer[]
  >([]);
  const [rankedProjections, setRankedProjections] = useState<
    NormalizedProjectionWithPlayer[]
  >([]);

  const {
    data: unrankedProjectionsDataResponse,
    isLoading: unrankedProjectionsLoading,
    refetch: refetchUnrankedProjections
  } = useGetStreakProjectionsQuery({
    date: selectedDateString,
    textSearch: debouncedTextSearch,
    isOnStreakBoard: false,
    leagueId: selectedLeagueId
  });

  const {
    data: rankedProjectionsDataResponse,
    isLoading: rankedProjectionsLoading,
    refetch: refetchRankedProjections
  } = useGetStreakProjectionsQuery({
    date: selectedDateString,
    isOnStreakBoard: true
  });

  // Update local state when data responses change
  useEffect(() => {
    if (unrankedProjectionsDataResponse) {
      setUnrankedProjections(unrankedProjectionsDataResponse);
    }
  }, [unrankedProjectionsDataResponse]);

  useEffect(() => {
    if (rankedProjectionsDataResponse) {
      const sortedRankedProjections = [...rankedProjectionsDataResponse].sort(
        (a, b) => a.attributes.streak_rank! - b.attributes.streak_rank!
      );
      setRankedProjections(sortedRankedProjections);
    }
  }, [rankedProjectionsDataResponse]);

  // Functions to move projections between lists
  const moveProjectionToRanked = (projection: StreakProjectionItem) => {
    setUnrankedProjections((prev: StreakProjectionItem[]) =>
      prev.filter((p: StreakProjectionItem) => p.id !== projection.id)
    );
    setRankedProjections((prev: StreakProjectionItem[]) => [
      ...prev,
      projection
    ]);
  };

  const moveProjectionToUnranked = (projection: StreakProjectionItem) => {
    setRankedProjections((prev: StreakProjectionItem[]) =>
      prev.filter((p: StreakProjectionItem) => p.id !== projection.id)
    );
    setUnrankedProjections((prev: StreakProjectionItem[]) => [
      ...prev,
      projection
    ]);
  };

  const unrankedProjectionsToDisplay = useMemo(
    () =>
      unrankedProjections.filter(
        (projection: StreakProjectionItem) =>
          !rankedProjections.find(
            (p: StreakProjectionItem) => p.id === projection.id
          )
      ),
    [unrankedProjections, rankedProjections]
  );

  const rankedProjectionsToDisplay = useMemo(
    () =>
      rankedProjections.filter(
        (projection: StreakProjectionItem) =>
          !unrankedProjections.find(
            (p: StreakProjectionItem) => p.id === projection.id
          )
      ),
    [unrankedProjections, rankedProjections]
  );

  return {
    unrankedProjectionsToDisplay,
    setUnrankedProjections,
    unrankedProjectionsLoading,
    refetchUnrankedProjections,
    rankedProjectionsToDisplay,
    setRankedProjections,
    rankedProjectionsLoading,
    refetchRankedProjections,
    moveProjectionToRanked,
    moveProjectionToUnranked
  };
};
